import { AlertContainer } from "@nova/component-alert-container"
import { ButtonTertiary, ButtonPrimary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { Input } from "@nova/ds-component-input"
import { SavedContact } from "@nova/schemas"
import {
    formatPartialPhoneNumberInput,
    formatPhoneNumber,
    validateEmailAddress,
} from "@nova/util-strings"
import { useState, Dispatch, SetStateAction } from "react"

export const CreateSavedContactModal = ({
    confirm,
    cancel,
    cancelText = "Cancel",
    initialEmail,
    initialPhone,
    isPerformingAsyncConfirmCallback,
}: {
    confirm: (c: Pick<SavedContact, "name" | "email" | "phone">) => void
    cancel: () => void
    cancelText?: string
    initialEmail?: string
    initialPhone?: string
    isPerformingAsyncConfirmCallback: boolean
}) => {
    const [name, setName] = useState("")
    const [email, setEmail] = useState(initialEmail ?? "")
    const [phone, setPhone] = useState(initialPhone ?? "")
    const {
        showNoNameWarning,
        showNoContactMethodWarning,
        showInvalidPhoneNumberWarning,
        showInvalidEmailWarning,
        richSetter,
        validate,
    } = useValidateSavedContactForm()
    return (
        <HighPriorityNovaModalContainer
            title="Add Saved Contact"
            closeModal={cancel}
            className="w-[450px]"
            bottomBar={
                <div className="flex items-center justify-end gap-2 w-full">
                    <ButtonTertiary
                        title={cancelText}
                        onClick={cancel}
                        disabled={isPerformingAsyncConfirmCallback}
                    />
                    <ButtonPrimary
                        title="Save Contact"
                        onClick={() => {
                            if (!validate({ name, email, phone })) return
                            confirm({ name, email, phone })
                        }}
                        disabled={isPerformingAsyncConfirmCallback}
                    />
                </div>
            }
        >
            <SavedContactForm
                name={name}
                setName={(v) => richSetter(setName, v)}
                email={email}
                setEmail={(v) => richSetter(setEmail, v)}
                phone={phone}
                setPhone={(v) => richSetter(setPhone, v)}
                disabled={isPerformingAsyncConfirmCallback}
                showNoNameWarning={showNoNameWarning}
                showNoContactMethodWarning={showNoContactMethodWarning}
                showInvalidPhoneNumberWarning={showInvalidPhoneNumberWarning}
                showInvalidEmailWarning={showInvalidEmailWarning}
            />
        </HighPriorityNovaModalContainer>
    )
}

export const EditSavedContactModal = ({
    contact,
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback,
}: {
    contact: SavedContact
    confirm: (c: SavedContact) => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
}) => {
    const [name, setName] = useState(contact.name)
    const [email, setEmail] = useState(contact.email ?? "")
    const [phone, setPhone] = useState(contact.phone ?? "")
    const {
        showNoNameWarning,
        showNoContactMethodWarning,
        showInvalidPhoneNumberWarning,
        showInvalidEmailWarning,
        richSetter,
        validate,
    } = useValidateSavedContactForm()
    return (
        <HighPriorityNovaModalContainer
            title="Edit Contact"
            closeModal={cancel}
            className="w-[450px]"
            bottomBar={
                <div className="flex items-center justify-end gap-2 w-full">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={cancel}
                        disabled={isPerformingAsyncConfirmCallback}
                    />
                    <ButtonPrimary
                        title="Save Contact"
                        onClick={() => {
                            if (!validate({ name, email, phone })) return
                            confirm({ ...contact, name, email, phone })
                        }}
                        disabled={isPerformingAsyncConfirmCallback}
                    />
                </div>
            }
        >
            <SavedContactForm
                name={name}
                setName={(v) => richSetter(setName, v)}
                email={email}
                setEmail={(v) => richSetter(setEmail, v)}
                phone={phone}
                setPhone={(v) => richSetter(setPhone, v)}
                disabled={isPerformingAsyncConfirmCallback}
                showNoNameWarning={showNoNameWarning}
                showNoContactMethodWarning={showNoContactMethodWarning}
                showInvalidPhoneNumberWarning={showInvalidPhoneNumberWarning}
                showInvalidEmailWarning={showInvalidEmailWarning}
            />
        </HighPriorityNovaModalContainer>
    )
}

const useValidateSavedContactForm = () => {
    const [showNoNameWarning, setShowNoNameWarning] = useState(false)
    const [showNoContactMethodWarning, setShowNoContactMethodWarning] = useState(false)
    const [showInvalidPhoneNumberWarning, setShowInvalidPhoneNumberWarning] = useState(false)
    const [showInvalidEmailWarning, setShowInvalidEmailWarning] = useState(false)
    const richSetter = (setter: Dispatch<SetStateAction<string>>, v: string) => {
        setter(v)
        setShowNoNameWarning(false)
        setShowNoContactMethodWarning(false)
        setShowInvalidPhoneNumberWarning(false)
        setShowInvalidEmailWarning(false)
    }
    const validate = ({ name, email, phone }: { name: string; email: string; phone: string }) => {
        if (!name) {
            setShowNoNameWarning(true)
            return false
        }
        if (!email && !phone) {
            setShowNoContactMethodWarning(true)
            return false
        }
        if (!!phone && !formatPhoneNumber(phone)) {
            setShowInvalidPhoneNumberWarning(true)
            return false
        }
        if (!!email && !validateEmailAddress(email)) {
            setShowInvalidEmailWarning(true)
            return false
        }
        return true
    }
    return {
        showNoNameWarning,
        showNoContactMethodWarning,
        showInvalidPhoneNumberWarning,
        showInvalidEmailWarning,
        richSetter,
        validate,
    }
}

const SavedContactForm = ({
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    disabled,
    showNoNameWarning,
    showNoContactMethodWarning,
    showInvalidPhoneNumberWarning,
    showInvalidEmailWarning,
}: {
    name: string
    setName: (s: string) => void
    email: string
    setEmail: (s: string) => void
    phone: string
    setPhone: (s: string) => void
    disabled: boolean
    showNoNameWarning: boolean
    showNoContactMethodWarning: boolean
    showInvalidPhoneNumberWarning: boolean
    showInvalidEmailWarning: boolean
}) => {
    return (
        <div className="flex flex-col gap-4">
            <Input
                value={name}
                onChange={(v) => setName(v.target.value)}
                label="Name*"
                disabled={disabled}
            />
            <Input
                value={phone}
                onChange={(v) => setPhone(formatPartialPhoneNumberInput(v.target.value))}
                label="Phone number"
                disabled={disabled}
            />
            <Input
                value={email}
                onChange={(v) => setEmail(v.target.value)}
                label="Email"
                disabled={disabled}
            />
            {showNoNameWarning && (
                <AlertContainer
                    type="warning"
                    title="Add a name for this contact."
                    className="mt-2"
                />
            )}
            {showNoContactMethodWarning && (
                <AlertContainer
                    type="warning"
                    title="Add a phone number or email."
                    className="mt-2"
                />
            )}
            {showInvalidPhoneNumberWarning && (
                <AlertContainer
                    type="warning"
                    title={`Enter a valid phone number${!!email ? ", or remove it to only save " + name + "'s email" : ""}.`}
                    className="mt-2"
                />
            )}
            {showInvalidEmailWarning && (
                <AlertContainer
                    type="warning"
                    title={`Enter a valid email${!!phone ? ", or remove it to only save " + name + "'s phone number" : ""}.`}
                    className="mt-2"
                />
            )}
        </div>
    )
}
